import React, { useCallback, useEffect, useState } from "react"

import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import FormLabel from "@mui/material/FormLabel"
import TextField from "@mui/material/TextField"
import CircularProgress from "@mui/material/CircularProgress"
import Skeleton from "@mui/material/Skeleton"
import Alert from "@mui/material/Alert"
import Snackbar from "@mui/material/Snackbar"
import Slide from "@mui/material/Slide"


// ---------------------------------------------------------------------------
// Error code / message → human-readable French text (same convention as
// IndexCard's friendlyError).
// ---------------------------------------------------------------------------
const friendlyError = (error) => {
    switch (error?.code) {
        case "session_not_found":
        case "PGRST301":
            return "Session expirée. Veuillez vous reconnecter via le lien magique reçu par email."
        case "over_email_send_rate_limit":
            return "Trop de tentatives. Veuillez réessayer dans quelques minutes."
        default:
            return error?.message
                ? `Erreur inattendue : ${error.message}`
                : "Une erreur inconnue est survenue. Veuillez réessayer."
    }
}


const CustomSnackbar = ({ open, setOpen, message, severity }) => (
    <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
        open={open}
        onClose={() => setOpen(false)}
        autoHideDuration={4000}
    >
        <Alert onClose={() => setOpen(false)} severity={severity} variant="outlined" sx={{ width: "100%" }}>
            {message}
        </Alert>
    </Snackbar>
)


// ---------------------------------------------------------------------------
// Loading skeleton — mirrors the card's layout to prevent layout shift
// ---------------------------------------------------------------------------
const ProfileCardSkeleton = () => (
    <Card variant="outlined" sx={{ padding: 0 }}>
        <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <Skeleton variant="text" width={160} height={24} sx={{ ml: 1 }} />
        </Box>
        <Stack spacing={2} sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={36} />
        </Stack>
    </Card>
)


// ---------------------------------------------------------------------------
// ProfilePage
//
// Standalone account page for users authenticated via Supabase's email +
// magic link flow (passwordless — there's no password field anywhere in
// this app). Lets the signed-in user update their display name.
//
// DECISION: the display name lives on the auth user itself
// (`user_metadata.display_name`, updated via `supabase.auth.updateUser`)
// rather than in a separate `profile` table, since nothing else in the
// schema currently needs to join on it. `updateUser({ data })` merges into
// existing metadata rather than replacing it, so this is safe to call even
// if other metadata keys get added later. If a `profile` table already
// exists elsewhere in this app, swap the fetch/save calls below for a query
// against it instead.
//
// Props
//   supabase – initialised Supabase client
// ---------------------------------------------------------------------------
const Account = ({ supabase }) => {
    const [email,       setEmail]       = useState("")
    const [displayName, setDisplayName] = useState("")
    const [initialName, setInitialName] = useState("")

    const [loading,    setLoading]    = useState(true)
    const [fetchError, setFetchError] = useState(null)

    const [saving,    setSaving]    = useState(false)
    const [nameError, setNameError] = useState("")
    const [snackbar,  setSnackbar]  = useState({ open: false, message: "", severity: "success" })

    const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity })

    const applyUser = useCallback((user) => {
        setEmail(user?.email ?? "")
        const name = user?.user_metadata?.display_name ?? ""
        setDisplayName(name)
        setInitialName(name)
    }, [])

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    // Magic-link sign-ins resolve asynchronously — the client parses the
    // token out of the URL *after* this component has already mounted — so
    // on top of the initial getUser() call we also listen for the resulting
    // SIGNED_IN event, the same way DashboardTable subscribes to realtime
    // changes instead of fetching only once.
    useEffect(() => {
        let active = true

        supabase.auth.getUser().then(({ data, error }) => {
            if (!active) return
            if (error) setFetchError(friendlyError(error))
            else applyUser(data.user)
            setLoading(false)
        })

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!session?.user) return
            applyUser(session.user)
            setFetchError(null)
            setLoading(false)
        })

        return () => {
            active = false
            subscription.unsubscribe()
        }
    }, [supabase, applyUser])

    // -----------------------------------------------------------------------
    // Save
    // -----------------------------------------------------------------------
    const handleNameChange = (e) => {
        setDisplayName(e.target.value)
        if (nameError) setNameError("")
    }

    const hasChanges = displayName.trim() !== "" && displayName.trim() !== initialName

    const handleSave = async () => {
        const trimmed = displayName.trim()
        if (!trimmed) {
            setNameError("Le nom d'affichage ne peut pas être vide.")
            return
        }
        if (!hasChanges) return

        setSaving(true)
        const { error } = await supabase.auth.updateUser({ data: { display_name: trimmed } })

        if (error) {
            showSnackbar(friendlyError(error), "error")
        } else {
            setDisplayName(trimmed)
            setInitialName(trimmed)
            showSnackbar("Nom d'affichage mis à jour avec succès")
        }
        setSaving(false)
    }


    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <Box sx={{ width: "100%", maxWidth: 480, mx: "auto", mt: 4 }}>
            <Typography component="h2" variant="h4" sx={{ mb: 2 }}>
                Mon profil
            </Typography>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />

            {loading ? (
                <ProfileCardSkeleton />
            ) : fetchError ? (
                <Alert severity="error" variant="outlined">{fetchError}</Alert>
            ) : !email ? (
                <Alert severity="warning" variant="outlined">
                    Vous devez être connecté pour accéder à cette page.
                </Alert>
            ) : (
                <Card variant="outlined" sx={{ padding: 0 }}>
                    <Box sx={{ backgroundColor: "background.paper", p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography component="p" sx={{ fontSize: 16, fontWeight: 600, paddingLeft: 1 }}>
                            Informations du compte
                        </Typography>
                    </Box>
                    <Stack spacing={2} sx={{ p: 2 }}>
                        <Box>
                            <FormLabel htmlFor="email">Adresse email</FormLabel>
                            <TextField
                                fullWidth id="email" value={email} variant="outlined" size="small"
                                disabled sx={{ mt: 0.5 }}
                                helperText="Connexion par lien magique — l'adresse ne peut pas être modifiée ici."
                            />
                        </Box>
                        <Box>
                            <FormLabel htmlFor="displayName">Nom d'affichage</FormLabel>
                            <TextField
                                fullWidth id="displayName" value={displayName} onChange={handleNameChange}
                                onKeyDown={(e) => { if (e.key === "Enter") handleSave() }}
                                variant="outlined" size="small" sx={{ mt: 0.5 }}
                                error={!!nameError}
                                helperText={nameError || " "}
                            />
                        </Box>
                        <Button
                            variant="contained" fullWidth onClick={handleSave}
                            disabled={saving || !hasChanges}
                        >
                            {saving ? <CircularProgress size={20} color="inherit" /> : "Enregistrer"}
                        </Button>
                    </Stack>
                </Card>
            )}
        </Box>
    )
}

export default Account